import { isEmbeddedApp } from '@thoughtspot/embed-util';
import { IconSize } from '@thoughtspot/radiant-react/widgets/icon';
import { isRadiantThemeEnabled } from '@thoughtspot/session-service';
import { camelCase, parseInt } from 'lodash';
import {
    ChartType,
    ComponentType,
    GroupBrandColor,
    GroupCompositeStyle,
    GroupConfigStyle,
    LiveboardBrandColor,
    LiveboardCompositeStyle,
    LiveboardConfigStyle,
    PinboardContainerType,
    TileBrandColor,
    TileCompositeStyle,
    TileConfigStyle,
    TileKpiColor,
} from '/@services/generated/graphql-types';
import { SelectedTile } from '../../components/pinboard/pinboard-content/pinboard-tile-layout-utils';
import {
    DEFAULT_GROUP_GUTTER,
    DEFAULT_GROUP_PADDING,
    DEFAULT_LB_GUTTER,
    DEFAULT_TILE_PADDING,
    LIVEBOARD_STYLE_VARIABLES,
    RADIANT_RESET_VARIABLES,
} from './pinboard-style.constants';
import { LiveboardStyleContext, PureStyleConfigs } from './pinboard-style.type';
import {
    processToolbarConfig,
    StyleConfig,
    StyleConfigMap,
    ToolbarStyleConfig,
} from './style-property-mapper.util';

export enum ColorShade {
    DARK = 'DARK',
    LIGHT = 'LIGHT',
}

/**
 * Gets the config key for component overrides
 * @param componentType - The component type
 * @returns Config key string
 */
export const getConfigKeyForComponent = (
    componentType?: ComponentType,
): keyof StyleConfigMap => {
    switch (componentType) {
        case ComponentType.Group:
            return 'group_config';
        case ComponentType.Tile:
            return 'tile_config';
        default:
            return 'liveboard_config';
    }
};

export const transformFontWeight = (fontWeight: string) => {
    if (fontWeight === 'medium') {
        return 600;
    }
    return 375;
};

export const transformIconSize = (size: string) => {
    if (size === '24px') {
        return IconSize.SMALL;
    }
    return IconSize.MEDIUM;
};

/**
 * Transformers to modify style properties when default values need to be overridden.
 * For example, we want to convert font weight from string to number. like medium -> 600
 * @type {Record<string, (value: string) => any>}
 */
export const STYLE_TRANSFORMERS: Record<string, (value: string) => any> = {
    tileTitleFontWeight: transformFontWeight,
    groupTitleFontWeight: transformFontWeight,
    groupTileTitleFontWeight: transformFontWeight,
};

/**
 * Parses a string to a number, returning the default value if the string is not a valid number.
 * @param {number} defaultValue - The default value to return if the string is not a valid number.
 * @param {string} value - The string to parse.
 * @returns {number} The parsed number, or the default value if the string is not a valid number.
 */
export const parseNumber = (defaultValue: number, value: string) => {
    const parsed = parseInt(value);
    return Number.isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Transformers to modify configuration properties when default values need to be overridden.
 * For example, we want to convert string to number.
 * @type {Record<string, (value: any) => any>}
 */
export const CONFIG_TRANSFORMERS: Record<string, (value: any) => any> = {
    groupTilePadding: parseNumber.bind(null, DEFAULT_TILE_PADDING),
    groupPadding: parseNumber.bind(null, DEFAULT_GROUP_PADDING),
    groupGutter: parseNumber.bind(null, DEFAULT_GROUP_GUTTER),
    lbGutter: parseNumber.bind(null, DEFAULT_LB_GUTTER),
    iconSize: transformIconSize,
};

/**
 * This function is to process and map style properties to CSS variables.
 * Now supports multi-mapping where one YAML property can map to multiple CSS variables.
 * @param {Record<string, any>} pureStyle - Raw style object to process
 * @param {boolean} useTransformers - Whether to apply style transformers
 * @param {boolean} resetColorsForRadiant - Whether the radiant color-reset may
 *   apply to this style set. Pass `false` for user picks (component overrides)
 *   so their chosen colors are preserved verbatim instead of being reset to
 *   `initial`. `isRadiantThemeEnabled()` remains the master switch; this param
 *   only narrows it.
 * @returns {Record<string, any>} Processed style object with CSS variable mappings
 */
export function processStyles(
    pureStyle: Record<string, any> = {},
    useTransformers = true,
    resetColorsForRadiant = true,
): Record<string, any> {
    const styles: Record<string, any> = {};

    // When the radiant theme is enabled, saved per-liveboard COLOR/BACKGROUND
    // values are reset to `initial` so the component SCSS radiant token
    // fallbacks take precedence and surfaces adapt to light/dark mode.
    // Layout/typography vars are left untouched (see RADIANT_RESET_VARIABLES).
    // User picks opt out via `resetColorsForRadiant=false` so they render as-is.
    const resetColors = resetColorsForRadiant && isRadiantThemeEnabled();
    const resolveValue = (cssVariable: string, transformedValue: any) =>
        resetColors && RADIANT_RESET_VARIABLES.has(cssVariable)
            ? 'initial'
            : transformedValue;

    Object.entries(pureStyle).forEach(([key, value]) => {
        const cssVariables =
            LIVEBOARD_STYLE_VARIABLES[
                key as keyof typeof LIVEBOARD_STYLE_VARIABLES
            ];
        if (cssVariables) {
            const transformedValue =
                useTransformers && STYLE_TRANSFORMERS[key]
                    ? STYLE_TRANSFORMERS[key](value as string)
                    : value;

            if (Array.isArray(cssVariables)) {
                cssVariables.forEach(cssVariable => {
                    styles[cssVariable] = resolveValue(
                        cssVariable,
                        transformedValue,
                    );
                });
            } else {
                styles[cssVariables] = resolveValue(
                    cssVariables,
                    transformedValue,
                );
            }
        }
    });
    return styles;
}

export const applyScopedStylesIfRootIsInitial = (
    styles: Record<string, string>,
) => {
    if (!isEmbeddedApp() || !styles) {
        return styles;
    }

    const computedRootStyle = getComputedStyle(document.documentElement);

    const finalStyles: Record<string, string> = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const [varName, value] of Object.entries(styles)) {
        const rootValue = computedRootStyle.getPropertyValue(varName).trim();
        if (rootValue === 'initial' || !rootValue) {
            finalStyles[varName] = value;
        }
    }
    return finalStyles;
};

export const removeNullValues = (obj?: Record<string, any>) =>
    obj
        ? Object.fromEntries(
              Object.entries(obj).filter(
                  ([, value]) => value !== null && value !== undefined,
              ),
          )
        : undefined;

/**
 * Processes the given style object by mapping its properties to CSS variables
 * and then filters out css variables set in the root element(from TSE).
 * This ensures that only relevant and properly scoped styles are returned.
 * @param {Record<string, any>} styles - The raw style object to process and filter.
 * @returns {Record<string, any>} The processed and filtered style object.
 */
export const processAndFilterStyles = (
    styles: Record<string, any>,
    useTransformers = true,
    resetColorsForRadiant = true,
) => {
    const nonNullStyles = removeNullValues(styles);
    const processedStyles = processStyles(
        nonNullStyles,
        useTransformers,
        resetColorsForRadiant,
    );
    const filteredStyles = applyScopedStylesIfRootIsInitial(processedStyles);
    return filteredStyles;
};

/**
 * This function is to process configuration properties.
 * @param {Record<string, any>} configStyle - Raw configuration object to process
 * @returns {Record<string, any>} Processed configuration object with transformed values
 */
export function processConfigs(
    configStyle: Record<string, any> = {},
): Record<string, any> {
    return Object.entries(configStyle).reduce<Record<string, any>>(
        (acc, [key, value]) => {
            if (value === null || value === undefined) return acc;
            const transformer = CONFIG_TRANSFORMERS[key];
            acc[key] = transformer ? transformer(value) : value;
            return acc;
        },
        {},
    );
}

/**
 * User-pickable colors that are stored on `pureStyle` — both at the liveboard
 * level and on component overrides — that the radiant color reset would
 * otherwise wipe. Four preservation cases, all rooted in the fact that these
 * colors are reset to `initial` so DEFAULT surfaces adapt to light/dark mode,
 * which is wrong for genuine user picks:
 *
 * 1. Tile/group BACKGROUND: the backend fills the default (first / white
 *    palette color) even when the user only customised something unrelated, so
 *    presence is not a pick. Preserve only when it DIFFERS from a palette
 *    default (LBC_A / TBC_A / GBC_A); a default value stays reset and adapts.
 * 2. KPI hero color: the backend fills its default (a dark hex readable only
 *    in light mode) even without an explicit pick, so presence is not a pick
 *    here either. The product only offers a KPI colour picker on TILE
 *    overrides, so preservation is level-aware (`kpiColorCanBeUserPick`):
 *    - tile override: the composite enum (`compositeStyle.tileKpiColor`) is
 *      the ground truth — the picker (the only UI able to set a tile KPI
 *      colour) writes it and the pureStyle hex is derived from it. A valid
 *      TKS key = pick (preserve); an explicit TksUnknown = "Liveboard
 *      Default" (reset, even alongside a background pick — the stored hex
 *      is stale). The wire strips the composite, so an absent one is
 *      "unknown", not "no pick" — recovered via the edit-session registry,
 *      or inferred conservatively (hex outside getAutoKpiHexes = pick).
 *    - liveboard/group level: never preserve standalone — these levels have
 *      no KPI picker at all.
 *    Otherwise the value IS preserved when it accompanies a user-picked
 *    tile background (the backend coordinates the two; a reset hero could
 *    lose contrast on the preserved tile in dark mode). A reset value stays
 *    `initial`, so the hero inherits content-primary and adapts to
 *    light/dark mode.
 * 3. Coordinated tile text/border BUNDLE (title, description, border, KPI
 *    comparison/analyze): the composite generates these together to contrast a
 *    chosen background, so preserve them ONLY when the background is a user
 *    pick — they must stay with it (a light user tile's coordinated dark title
 *    legitimately equals the theme default, so a value-vs-default test would
 *    wrongly reset it and dark mode would then flip it to light-on-light).
 * 4. Liveboard CANVAS background (lbBgColor + its coordinated dot color): the
 *    FIRST palette swatch (LBC_A / #F6F8FA) is the theme "default" — it renders
 *    white in light mode and dark in dark mode — NOT a literal white. The
 *    backend stores that default value even on un-customized liveboards, so a
 *    stored value EQUAL to LBC_A's lbBgColor is left reset to `initial` and
 *    adapts to the theme, while any OTHER value is a literal user pick and is
 *    preserved verbatim. Detected INDEPENDENTLY of the tile background (case 3
 *    does not cover the canvas, and a liveboard pick may not carry a non-default
 *    tileBgColor). This is the one surface where "== default" must stay reset:
 *    honoring the default white would leave a light canvas behind dark tiles.
 */
const RADIANT_TILE_BG_KEYS = [
    'tileBgColor',
    'groupBgColor',
    'groupTileBgColor',
];

// KPI hero color synonyms (legacy + KPI style-override feature; both map to
// --ts-var-kpi-hero-color) — preserved per case 2.
const RADIANT_KPI_HERO_KEYS = ['kpiNumberFontColor', 'kpiHeroFontColor'];

// Coordinated colors preserved as a bundle when the tile background is a pick
// (case 3). Includes the background itself (case 1 is subsumed here).
const RADIANT_TILE_BUNDLE_KEYS = [
    ...RADIANT_TILE_BG_KEYS,
    'tileBorderColor',
    'groupBorderColor',
    'tileTitleFontColor',
    'groupTitleFontColor',
    'groupTileTitleFontColor',
    'tileDescriptionFontColor',
    'groupDescriptionFontColor',
    'groupTileDescriptionFontColor',
    'kpiComparisonFontColor',
    'kpiAnalyseFontColor',
];

// Liveboard CANVAS background + its coordinated dot color, preserved together
// when the canvas is a user pick (case 4).
const RADIANT_LIVEBOARD_BG_KEYS = ['lbBgColor', 'lbBgDotColor'];

// The liveboard CANVAS background default (the FIRST swatch, LBC_A's lbBgColor).
// A stored value equal to this is the theme "default" (white in light, dark in
// dark) and must stay reset; any other value is a literal user pick (case 4).
const getLiveboardBackgroundDefault = (map: any): string | undefined =>
    map?.liveboard_config?.lbBrandColor?.[LiveboardBrandColor.LbcA]
        ?.pure_style_mapping?.lbBgColor;

// Every candidate palette-default background hex (the first palette entry
// across the liveboard / tile / group configs).
const getTileBackgroundDefaults = (map: any): string[] =>
    [
        map?.liveboard_config?.lbBrandColor?.[LiveboardBrandColor.LbcA]
            ?.pure_style_mapping?.tileBgColor,
        map?.tile_config?.tileBrandColor?.[TileBrandColor.TbcA]
            ?.pure_style_mapping?.tileBgColor,
        map?.group_config?.groupBrandColor?.[GroupBrandColor.GbcA]
            ?.pure_style_mapping?.groupBgColor,
        map?.group_config?.groupBrandColor?.[GroupBrandColor.GbcA]
            ?.pure_style_mapping?.tileBgColor,
    ].filter((value): value is string => value != null);

// Tile composite registry — edit-session memory. The backend strips
// `compositeStyle` from wire liveboardStyle overrides, so the render
// transform consults this per-tile record (populated from the panel's own
// updates) when the wire composite is absent. Cleared when the edit ends.

const tileCompositeRegistry = new Map<string, TileCompositeStyle>();

/** Remembers a tile's composite as known by the edit session. */
export const rememberTileComposite = (
    tileId: string | undefined,
    compositeStyle: Partial<TileCompositeStyle> | undefined,
): void => {
    if (!tileId || !compositeStyle) return;
    tileCompositeRegistry.set(tileId, {
        ...tileCompositeRegistry.get(tileId),
        ...compositeStyle,
    });
};

/** The composite last known for a tile in this edit session, if any. */
export const getRememberedTileComposite = (
    tileId: string | undefined,
): TileCompositeStyle | undefined =>
    tileId ? tileCompositeRegistry.get(tileId) : undefined;

/** Forgets a single tile (e.g. its override was cleared by re-linking). */
export const forgetTileComposite = (tileId: string | undefined): void => {
    if (tileId) tileCompositeRegistry.delete(tileId);
};

/** Clears the whole registry — call when the edit session ends. */
export const clearTileCompositeRegistry = (): void => {
    tileCompositeRegistry.clear();
};

/**
 * Resolves the APPLIED value of a CSS variable for a tile from the style
 * context (what the tile actually renders). Precedence: tile > group >
 * liveboard, and a defined 'initial' wins at its level (= adaptive).
 * @param context - The liveboard style context (tracked state).
 * @param tileId - The tile's container id.
 * @param parentGroupId - The parent group's container id for nested tiles.
 * @param cssVar - The CSS variable name, e.g. '--ts-var-kpi-hero-color'.
 * @returns The applied value, or undefined when no level defines it.
 */
export const resolveAppliedTileStyleVar = (
    context: Partial<LiveboardStyleContext> | undefined,
    tileId: string | undefined,
    parentGroupId: string | undefined | null,
    cssVar: string,
): string | undefined => {
    const levels = [
        tileId ? context?.tiles?.[tileId]?.styles : undefined,
        parentGroupId ? context?.groups?.[parentGroupId]?.styles : undefined,
        context?.liveboard?.styles,
    ];
    const winner = levels.find(
        styles => styles && cssVar in styles && styles[cssVar] != null,
    );
    return winner?.[cssVar];
};

// Reverse lookup: the brand-color KEY whose pure_style_mapping[target]
// matches pureStyles[src] — e.g. which TBC_* key produced this tile
// background hex.
export const getCorrespondingBrandColorKey = (
    brandColors: Record<string, any>,
    pureStyles: Record<string, any>,
    src: string,
    target: string,
) => {
    if (!brandColors || !pureStyles?.[src]) return undefined;

    return Object.entries(brandColors).find(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, value]) => value?.pure_style_mapping?.[target] === pureStyles[src],
    )?.[0];
};

// Every hex the system itself can write as an "auto" KPI hero, from the same
// config-map payload (no cross-release drift). A composite-less KPI hex
// outside this set can only be a user pick.
const getAutoKpiHexes = (map: any): Set<string> => {
    const hexes = new Set<string>();
    const add = (value?: string) => {
        if (typeof value === 'string' && value) {
            hexes.add(value.toLowerCase());
        }
    };

    const tksDefault =
        map?.tile_config?.tileKpiColor?.[TileKpiColor.TksA]?.pure_style_mapping;
    add(tksDefault?.kpiNumberFontColor);
    add(tksDefault?.colorValue);

    Object.values(
        map?.tile_config?.tileBrandColor ?? {},
    ).forEach((entry: any) =>
        add(entry?.pure_style_mapping?.kpiNumberFontColor),
    );
    Object.values(
        map?.tile_config?.tileColorShade ?? {},
    ).forEach((entry: any) =>
        add(entry?.pure_style_mapping?.kpiNumberFontColor),
    );

    return hexes;
};

/**
 * Options describing how a pureStyle set is processed for radiant — which
 * transformations apply and how KPI hero preservation is decided at this
 * level (see case 2 in the comment block above).
 */
export interface RadiantStyleOptions {
    /** Whether to apply style transformers (must match the caller's own
     *  processing). Defaults to true. */
    useTransformers?: boolean;
    /** Whether this level offers a KPI colour picker — true only for TILE
     *  overrides. Defaults to false. */
    kpiColorCanBeUserPick?: boolean;
    /** The tile override's `compositeStyle.tileKpiColor`, when available.
     *  The composite is the ground truth for a KPI pick: a valid TKS key
     *  means a pick, an explicit TksUnknown means "Liveboard Default". */
    tileKpiComposite?: TileKpiColor;
}

/**
 * Returns the CSS-variable overrides that rescue user-picked colors from the
 * radiant color reset. See RADIANT_*_KEYS above for the three cases; backend
 * defaults stay reset so they adapt to light/dark mode.
 * @param rawPureStyle - The raw `pureStyle` (pre-reset).
 * @param styleConfigMapObject - The style config map holding palette defaults.
 * @param options - Processing and KPI-preservation options for this level.
 * @returns {Record<string, string>} CSS var → preserved value (may be empty).
 */
export const getPreservedLiveboardColorPicks = (
    rawPureStyle: Record<string, any> | undefined,
    styleConfigMapObject: any,
    {
        useTransformers = true,
        kpiColorCanBeUserPick = false,
        tileKpiComposite,
    }: RadiantStyleOptions = {},
): Record<string, string> => {
    if (!rawPureStyle || !styleConfigMapObject) return {};

    // Non-reset processing keeps the raw hex values (and, in embedded apps,
    // still honours the host's `customCSS.variables` via the scoped filter).
    const nonReset = processAndFilterStyles(
        rawPureStyle,
        useTransformers,
        false,
    );
    const preserved: Record<string, string> = {};

    const preserveByRawKey = (rawKey: string) => {
        const cssVar =
            LIVEBOARD_STYLE_VARIABLES[
                rawKey as keyof typeof LIVEBOARD_STYLE_VARIABLES
            ];
        if (typeof cssVar === 'string' && cssVar in nonReset) {
            preserved[cssVar] = nonReset[cssVar];
        }
    };

    // Case 1 + 3: preserve the tile background and its coordinated bundle only
    // when the background differs from the backend palette default (a user
    // pick).
    const bgValue = RADIANT_TILE_BG_KEYS.map(key => rawPureStyle[key]).find(
        value => value != null && value !== '',
    );
    const bgDefaults = getTileBackgroundDefaults(styleConfigMapObject);
    const bgIsUserPick = bgValue != null && !bgDefaults.includes(bgValue);

    if (bgIsUserPick) {
        RADIANT_TILE_BUNDLE_KEYS.forEach(preserveByRawKey);
    }

    // Case 2: KPI hero color — level-aware (see the comment block above).
    // The composite enum is the GROUND TRUTH for a KPI pick: the picker (the
    // only UI that can set a tile KPI colour) writes `tileKpiColor` and the
    // pureStyle hex is derived from it. A valid TKS key = pick; an explicit
    // TksUnknown = "Liveboard Default"; an ABSENT composite means no UI pick
    // was ever made (the hex is backend-filled). The liveboard/group levels
    // have no KPI picker at all. Hex-vs-default comparison is deliberately
    // NOT used — default hexes drift across releases/clusters, so it cannot
    // tell a pick apart from a stale backend default.
    const kpiIsUserPick =
        kpiColorCanBeUserPick &&
        tileKpiComposite != null &&
        tileKpiComposite !== TileKpiColor.TksUnknown;

    // An explicit TksUnknown means the user chose "Liveboard Default" — a
    // stored hex is stale and must not ride along with a background pick.
    const kpiExplicitlyDefault = tileKpiComposite === TileKpiColor.TksUnknown;

    // Composite UNKNOWN (wire strips it — not an explicit TksUnknown):
    // conservatively infer a pick when the hex is outside getAutoKpiHexes;
    // ambiguous hexes stay reset (the registry supplies exactness in-session).
    const rawKpiHex = RADIANT_KPI_HERO_KEYS.map(key => rawPureStyle[key]).find(
        value => value != null && value !== '',
    );
    const kpiImplicitPick =
        kpiColorCanBeUserPick &&
        tileKpiComposite == null &&
        rawKpiHex != null &&
        !getAutoKpiHexes(styleConfigMapObject).has(
            String(rawKpiHex).toLowerCase(),
        );

    if (
        (bgIsUserPick && !kpiExplicitlyDefault) ||
        kpiIsUserPick ||
        kpiImplicitPick
    ) {
        RADIANT_KPI_HERO_KEYS.forEach(preserveByRawKey);
    }

    // Composite proves a pick but pureStyle lacks the hex (partial optimistic
    // data / bg reset blanked it): resolve the hex from the config map.
    const kpiCssVar = LIVEBOARD_STYLE_VARIABLES.kpiNumberFontColor as string;
    if (kpiIsUserPick && !preserved[kpiCssVar]) {
        const mappedKpiHex =
            styleConfigMapObject?.tile_config?.tileKpiColor?.[tileKpiComposite]
                ?.pure_style_mapping?.kpiNumberFontColor;
        if (mappedKpiHex) {
            Object.assign(
                preserved,
                processAndFilterStyles(
                    { kpiNumberFontColor: mappedKpiHex },
                    useTransformers,
                    false,
                ),
            );
        }
    }

    // An explicit reset must emit a value (coordinated hero on a picked
    // background, else `initial`) — the context's deep-merge skips empty
    // updates, so silence would let the previous pick's hex survive.
    if (kpiColorCanBeUserPick && kpiExplicitlyDefault) {
        const bgBrandKey = getCorrespondingBrandColorKey(
            styleConfigMapObject?.tile_config?.tileBrandColor,
            rawPureStyle,
            'tileBgColor',
            'tileBgColor',
        );
        const coordinatedHero = bgIsUserPick
            ? styleConfigMapObject?.tile_config?.tileBrandColor?.[bgBrandKey]
                  ?.pure_style_mapping?.kpiNumberFontColor
            : undefined;
        if (coordinatedHero) {
            Object.assign(
                preserved,
                processAndFilterStyles(
                    { kpiNumberFontColor: coordinatedHero },
                    useTransformers,
                    false,
                ),
            );
        } else {
            // Root-filtered like every other write: a TSE-host-set
            // --ts-var-kpi-hero-color must not be shadowed by the reset.
            Object.assign(
                preserved,
                applyScopedStylesIfRootIsInitial({ [kpiCssVar]: 'initial' }),
            );
        }
    }

    // Case 4: preserve the liveboard canvas background independently, but only
    // when it DIFFERS from the first swatch (LBC_A). That first swatch is the
    // theme "default" (white in light, dark in dark), stored even on
    // un-customized liveboards, so a value equal to it stays reset and adapts;
    // any other value is a literal user pick. It is detected here because a
    // whole-liveboard "Colour" pick may not carry a non-default tile background
    // and so cannot ride on the tile bundle above.
    const lbBgValue = rawPureStyle.lbBgColor;
    const lbBgDefault = getLiveboardBackgroundDefault(styleConfigMapObject);
    const lbBgIsUserPick =
        lbBgValue != null && lbBgValue !== '' && lbBgValue !== lbBgDefault;

    if (lbBgIsUserPick) {
        RADIANT_LIVEBOARD_BG_KEYS.forEach(preserveByRawKey);
    }

    return preserved;
};

/**
 * Processes a raw `pureStyle` for radiant: default palette colors are reset to
 * `initial` (so surfaces adapt to light/dark mode) while genuine user picks —
 * non-default tile/group backgrounds with their coordinated bundles, the
 * liveboard canvas, and composite-backed KPI colours — are preserved verbatim.
 * When radiant is off this is just `processAndFilterStyles`. Shared by the
 * liveboard base theme and per tile/group component overrides.
 * @param rawPureStyle - The raw pure-style object to process.
 * @param styleConfigMapObject - The style config map holding palette defaults.
 * @param options - Processing and KPI-preservation options for this level.
 * @returns {Record<string, any>} Processed style object with CSS variables.
 */
export const processRadiantAwareStyles = (
    rawPureStyle: Record<string, any> | undefined,
    styleConfigMapObject: any,
    options: RadiantStyleOptions = {},
): Record<string, any> => {
    const styles = processAndFilterStyles(
        rawPureStyle ?? {},
        options.useTransformers ?? true,
    );
    if (isRadiantThemeEnabled()) {
        Object.assign(
            styles,
            getPreservedLiveboardColorPicks(
                rawPureStyle,
                styleConfigMapObject,
                options,
            ),
        );
    }
    return styles;
};

/**
 * This function is to process component override styles and configurations.
 * @param {any} override - Component override object containing type and style information
 * @param {any} defaultStyle - Default style object to use for fallback values
 * @param {any} styleConfigMapObject - Style config map holding palette defaults,
 *   used to tell user-picked colors apart from backend defaults under radiant.
 * @returns {[string, any]} Tuple containing component ID and processed styles/configs
 */
export function processComponentOverride(
    override: any,
    defaultStyle?: any,
    styleConfigMapObject?: any,
): [string, any] {
    const isGroup = override.type === ComponentType.Group;
    if (isGroup) {
        const isLinked = override.groupStyle.configStyle?.isLinked ?? true;
        const groupStyle = {
            ...override.groupStyle,
            pureStyle: {
                ...override.groupStyle.pureStyle,
                tileBgColor:
                    override.groupStyle.pureStyle?.tileBgColor ||
                    override.groupStyle.pureStyle?.groupBgColor,
                tileBorderColor:
                    override.groupStyle.pureStyle?.tileBorderColor ||
                    override.groupStyle.pureStyle?.groupBorderColor,
            },
            ...(!isLinked && {
                configStyle: {
                    ...override.groupStyle.configStyle,
                    hideGroupTitle:
                        override.groupStyle.configStyle?.hideGroupTitle ??
                        defaultStyle?.configStyle?.hideGroupTitle,
                    hideGroupDescription:
                        override.groupStyle.configStyle?.hideGroupDescription ??
                        defaultStyle?.configStyle?.hideGroupDescription,
                    hideGroupDivider:
                        override.groupStyle.configStyle?.hideGroupTitle ??
                        defaultStyle?.configStyle?.hideGroupTitle,
                    hideGroupTileDescription:
                        override.groupStyle.configStyle
                            ?.hideGroupTileDescription ??
                        defaultStyle?.configStyle?.hideGroupTileDescription,
                    kpiHeroFontSize:
                        override.groupStyle.configStyle?.kpiHeroFontSize ??
                        defaultStyle?.configStyle?.kpiHeroFontSize,
                },
            }),
        };

        return [
            override.id,
            {
                id: override.id,
                // Reset backend default colors so surfaces adapt to dark mode,
                // but preserve any user pick that differs from the default.
                styles: processRadiantAwareStyles(
                    groupStyle.pureStyle,
                    styleConfigMapObject,
                    { useTransformers: false },
                ),
                configs: !override.clear
                    ? processConfigs(groupStyle.configStyle)
                    : {},
                clear: override.clear,
            },
        ];
    }

    return [
        override.id,
        {
            id: override.id,
            // Reset backend default colors so surfaces adapt to dark mode, but
            // preserve any user pick that differs from the default. Tile
            // overrides are the only level with a KPI colour picker, so a KPI
            // value here can be a genuine pick — the composite enum is the
            // ground truth for that. The wire strips the composite from
            // liveboardStyle overrides, so fall back to the edit-session
            // registry (populated by the styling panel's own updates).
            styles: processRadiantAwareStyles(
                override?.tileStyle?.pureStyle,
                styleConfigMapObject,
                {
                    useTransformers: false,
                    kpiColorCanBeUserPick: true,
                    tileKpiComposite:
                        override?.tileStyle?.compositeStyle?.tileKpiColor ??
                        getRememberedTileComposite(override?.id)?.tileKpiColor,
                },
            ),
            configs: processConfigs(override?.tileStyle?.configStyle),
            clear: override.clear,
        },
    ];
}

export type BrandColorMapping = {
    [key: string]: string;
};

export const getTileColorShade = (tileColor: string) => {
    if (!tileColor) return undefined;
    // if group brand color is between 'GBC_A' to GBC_E then it is light else dark
    // if liveboar brand color is between 'A' to H then it is light
    if (
        (tileColor >= TileBrandColor.TbcA &&
            tileColor <= TileBrandColor.TbcH) ||
        (tileColor >= GroupBrandColor.GbcA &&
            tileColor <= GroupBrandColor.GbcH) ||
        (tileColor >= LiveboardBrandColor.LbcA &&
            tileColor <= LiveboardBrandColor.LbcH)
    ) {
        return ColorShade.LIGHT;
    }
    return ColorShade.DARK;
};

export const getKeyByHexValue = (
    colorMapping: BrandColorMapping,
    hexValue: string,
): any => {
    return Object.keys(colorMapping).find(
        key => colorMapping[key] === hexValue,
    );
};

export const getColorShadeByHexValue = (
    colorMapping: BrandColorMapping,
    hexValue: string,
): ColorShade => {
    const shade = getTileColorShade(getKeyByHexValue(colorMapping, hexValue));
    return shade;
};

/**
 * This function is to transform the liveboard theme data to the LiveboardStyleContext type.
 * @param theme - The liveboard theme data
 * @param containers - Optional array of pinboard containers to check tile types
 * @param styleConfigMapObject - Optional style config map for color validation
 * @returns {LiveboardStyleContext} The transformed liveboard theme data
 */
export function transformLiveboardThemeData(
    theme: any,
    containers: any[],
    styleConfigMapObject: any,
): LiveboardStyleContext {
    // When radiant is enabled, the legacy "fix dark tile colors" pass below is
    // skipped: it would force-replace dark saved tiles with the light TBC_A
    // palette, re-clobbering the `initial` reset and breaking dark mode. The
    // radiant `rd-sys-color()` SCSS fallbacks already handle dark surfaces.
    const radiantEnabled = isRadiantThemeEnabled();
    // Reset backend default colors so surfaces adapt to light/dark mode, while
    // preserving any liveboard-wide user pick that differs from the default.
    const styles = processRadiantAwareStyles(
        theme?.defaultStyle?.pureStyle,
        styleConfigMapObject,
    );
    const pureStyleConfigs = processConfigs({
        groupTilePadding: theme?.defaultStyle?.pureStyle?.groupTilePadding,
        groupPadding: theme?.defaultStyle?.pureStyle?.groupPadding,
        iconSize: theme?.defaultStyle?.pureStyle?.iconSize,
    }) as PureStyleConfigs;
    const configs = processConfigs(theme?.defaultStyle?.configStyle);
    const transformed: LiveboardStyleContext = {
        id: 'theme-id',
        name: theme?.name,
        liveboard: {
            id: theme?.id,
            styles,
            pureStyleConfigs,
            configs,
        },
        groups: {},
        tiles: {},
    };

    if (!styleConfigMapObject) {
        return transformed;
    }

    theme?.componentOverrides?.forEach((override: any) => {
        const [id, component] = processComponentOverride(
            override,
            theme?.defaultStyle,
            styleConfigMapObject,
        );
        if (override.type === ComponentType.Group) {
            transformed.groups[id] = component;
        } else if (override.type === ComponentType.Tile) {
            transformed.tiles[id] = component;

            // Check and fix dark colors for non-KPI tiles
            if (containers) {
                const container = containers.find(c => c?.id === override.id);
                const isKpiTile = container?.chartType === ChartType.Kpi;

                if (!radiantEnabled && container && !isKpiTile) {
                    const tileBgColor =
                        transformed.tiles[id].styles[
                            LIVEBOARD_STYLE_VARIABLES.tileBgColor as string
                        ];

                    // Get tile color palette mapping for dark color validation
                    const tileColorPaletteMapping = styleConfigMapObject
                        ?.tile_config?.tileBrandColor
                        ? Object.fromEntries(
                              Object.entries(
                                  styleConfigMapObject.tile_config
                                      .tileBrandColor,
                              ).map(([key, value]: [string, any]) => [
                                  key,
                                  value?.pure_style_mapping?.tileBgColor,
                              ]),
                          )
                        : null;

                    if (tileBgColor && tileColorPaletteMapping) {
                        const bgColorShade = getColorShadeByHexValue(
                            tileColorPaletteMapping,
                            tileBgColor,
                        );

                        if (bgColorShade && bgColorShade === ColorShade.DARK) {
                            // Reset to TBC_A (first light color) with ALL its associated styles
                            const { pureStyle } = processToolbarConfig(
                                styleConfigMapObject?.tile_config,
                                {
                                    tileBrandColor: TileBrandColor.TbcA,
                                },
                            );

                            // Convert camelCase pureStyle to CSS variable names and merge
                            const cssVariableStyles = processStyles(
                                pureStyle,
                                false,
                            );
                            transformed.tiles[id].styles = {
                                ...transformed.tiles[id].styles,
                                ...cssVariableStyles,
                            };
                        }
                    }
                }
            }
        }
    });

    return transformed;
}

export const onlyGroupSelected = (selectedTiles: SelectedTile[]) => {
    return (
        selectedTiles?.length === 1 &&
        selectedTiles[0]?.type === PinboardContainerType.Group
    );
};

export const onlyTileSelected = (selectedTiles: SelectedTile[]) => {
    return (
        selectedTiles?.length === 1 &&
        selectedTiles[0]?.type === PinboardContainerType.PinnedAnswer
    );
};

/**
 * Finds a matching color object based on the color value
 * @param colorMapping - The color configuration mapping
 * @param colorValue - The color value to match against
 * @param mappingKey - The key to match against
 * @returns The matching color config object or undefined if not found
 */
export const findMatchingColorObject = (
    colorMapping: StyleConfig,
    colorValue: string,
    mappingKey: string,
): ToolbarStyleConfig => {
    if (!colorMapping || !colorValue) return undefined;

    return Object.values(colorMapping).find(
        colorObj => colorObj?.pure_style_mapping?.[mappingKey] === colorValue,
    );
};

/**
 * Finds the highlight tile color based on the current liveboard background color
 * @param liveboardColors - The liveboard color configuration mapping
 * @param currentBgColor - The current background color of the liveboard
 * @returns The highlight tile color or TbcUnknown if not found
 */
export const findHighlightTileColor = (
    matchingColorObject: ToolbarStyleConfig,
): TileBrandColor => {
    const highlightTileColor =
        matchingColorObject?.composite_style_mapping
            ?.highlightedTileBrandColor || TileBrandColor.TbcUnknown;

    return highlightTileColor as TileBrandColor;
};

/**
 * Resets all values in a pureStyle object to empty strings while preserving the keys.
 * This is useful when resetting tile styles to their default state.
 * @param {Record<string, any>} pureStyle - The pureStyle object to reset
 * @returns {StyleResult} An object with pureStyle and configStyle keys containing empty values
 */
export const resetAllPureStyles = (
    pureStyle: Record<string, any>,
): Record<string, any> => {
    if (!pureStyle) {
        return {};
    }

    return {
        pureStyle: Object.keys(pureStyle).reduce(
            (acc, key) => ({
                ...acc,
                [key]: '',
            }),
            {},
        ),
    };
};

export type StylePanelConfig = {
    liveboardConfig: {
        compositeStyle: LiveboardCompositeStyle;
        configStyle?: LiveboardConfigStyle;
    };
    groupConfig: {
        compositeStyle: GroupCompositeStyle;
        configStyle?: GroupConfigStyle;
    };
    tileConfig: {
        compositeStyle: TileCompositeStyle;
        configStyle?: TileConfigStyle;
    };
    toolbarConfigMapping: string;
    defaultTileBgColor: string;
    defaultKpiHeroColor: string;
};

export const mapConfigToStylePanelConfig = (config: any) => {
    return {
        liveboardConfig: {
            ...config.liveboardConfig,
            configStyle: removeNullValues({
                ...config.liveboardConfig?.configStyle,
                hideGroupDescription:
                    config.liveboardConfig?.configStyle?.hideGroupTitle ||
                    config.liveboardConfig?.configStyle?.hideGroupDescription,
                hideGroupDivider:
                    config.liveboardConfig?.configStyle?.hideGroupTitle,
                kpiHeroFontSize:
                    config.liveboardConfig?.configStyle?.kpiHeroFontSize,
            }),
        },
        groupConfig: config.groupConfig
            ? {
                  ...config.groupConfig,
                  configStyle: removeNullValues({
                      hideGroupTitle:
                          config.groupConfig?.configStyle?.hideGroupTitle ??
                          config?.defaultGroupConfig?.configStyle
                              ?.hideGroupTitle,
                      hideGroupDescription:
                          (config.groupConfig?.configStyle?.hideGroupTitle ??
                              config?.defaultGroupConfig?.configStyle
                                  ?.hideGroupTitle) ||
                          (config.groupConfig?.configStyle
                              ?.hideGroupDescription ??
                              config?.defaultGroupConfig?.configStyle
                                  ?.hideGroupDescription),
                      hideGroupTileDescription:
                          config.groupConfig?.configStyle
                              ?.hideGroupTileDescription ??
                          config?.defaultGroupConfig?.configStyle
                              ?.hideGroupTileDescription,
                      hideGroupDivider:
                          config.groupConfig?.configStyle?.hideGroupTitle ??
                          config?.defaultGroupConfig?.configStyle
                              ?.hideGroupTitle,
                      kpiHeroFontSize:
                          config.groupConfig?.configStyle?.kpiHeroFontSize ??
                          config?.defaultGroupConfig?.configStyle
                              ?.kpiHeroFontSize,
                      isLinked:
                          config.groupConfig?.configStyle?.isLinked ?? true,
                  }),
              }
            : config.defaultGroupConfig,
        tileConfig: config.tileConfig
            ? {
                  ...config.tileConfig,
                  configStyle: removeNullValues({
                      hideTileDescription:
                          config.tileConfig?.configStyle?.hideTileDescription ??
                          config?.defaultTileConfig?.configStyle
                              ?.hideTileDescription,
                      isHighlighted:
                          config.tileConfig?.configStyle?.isHighlighted,
                      kpiHeroFontSize:
                          config.tileConfig?.configStyle?.kpiHeroFontSize ??
                          config?.defaultTileConfig?.configStyle
                              ?.kpiHeroFontSize,
                      isLinked:
                          config.tileConfig?.configStyle?.isLinked ?? true,
                  }),
              }
            : config.defaultTileConfig,
        toolbarConfigMapping: config.toolbarConfigMapping,
        defaultTileBgColor:
            config.defaultTileConfig?.pureStyle?.tileBgColor ||
            config.defaultGroupConfig?.pureStyle?.groupBgColor,
        defaultKpiHeroColor:
            config.defaultTileConfig?.pureStyle?.kpiNumberFontColor,
    };
};

const keyMapForMixpanel: Record<string, string> = {
    lbBrandColor: 'color',
    lbBorderType: 'cornerStyle',
    lbDensity: 'density',
    lbGutterType: 'gutter',
    kpiHeroFontSize: 'KPI_size',
};

export const mapStyleConfigKeysForMixpanel = (config: any) => {
    const mappedConfig: Record<string, any> = {};

    Object.entries(config).forEach(([key, value]) => {
        mappedConfig.propertyName = keyMapForMixpanel[key] || key;
        mappedConfig.propertyValue = value;
    });

    return mappedConfig;
};

/**
 * Recursively converts all keys in an object from snake_case to camelCase.
 * Handles nested objects and arrays.
 *
 * @param obj - The object to convert
 * @returns A new object with all keys converted to camelCase
 */
/**
 * Marks stale component overrides for clearing.
 * Any tile/group ID present in the current context state but absent in the
 * new API data is injected with `{ clear: true }` so that `mergeComponents`
 * removes it instead of letting the old entry persist.
 */
export function clearRemovedOverrides(
    newStyle: LiveboardStyleContext,
    currentStyle: LiveboardStyleContext | undefined,
): LiveboardStyleContext {
    if (!currentStyle) return newStyle;

    const clearEntry = (id: string) => ({
        id,
        clear: true,
        styles: {},
        configs: {},
    });

    const staleTiles = Object.keys(currentStyle.tiles ?? {}).reduce(
        (acc, id) => {
            if (!newStyle.tiles[id]) {
                acc[id] = clearEntry(id);
            }
            return acc;
        },
        {} as Record<string, any>,
    );

    const staleGroups = Object.keys(currentStyle.groups ?? {}).reduce(
        (acc, id) => {
            if (!newStyle.groups[id]) {
                acc[id] = clearEntry(id);
            }
            return acc;
        },
        {} as Record<string, any>,
    );

    return {
        ...newStyle,
        tiles: { ...newStyle.tiles, ...staleTiles },
        groups: { ...newStyle.groups, ...staleGroups },
    };
}

export const convertKeysToCamelCase = <T = any>(obj: any): T => {
    if (Array.isArray(obj)) {
        return obj.map(convertKeysToCamelCase) as T;
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((result, key) => {
            const camelKey = camelCase(key);
            return {
                ...result,
                [camelKey]: convertKeysToCamelCase(obj[key]),
            };
        }, {} as any) as T;
    }
    return obj;
};
